import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, KeyRound, User, Building2, Eye, EyeOff } from 'lucide-react';

import caregiverService from '../../services/caregiverService';
import flexcoaLogo from '../../assets/images/flexcoa-logo-condensed-green.png';

/**
 * Caregiver login screen (FECOA-1320).
 *
 * Caregivers authenticate with `caregiver_id` + PIN. Sponsor scope is read
 * from the URL hash (e.g. `/caregiver-login#sp-acme`) so an SC can hand out
 * a single sponsor-scoped link. If no sponsor hash is present we expose a
 * plain Sponsor input.
 */
const CaregiverLogin = () => {
  const navigate = useNavigate();
  const [caregiverId, setCaregiverId] = useState('');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [sponsorId, setSponsorId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const sponsorPrefilled = window.location.hash.length > 1;

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#')) {
      setSponsorId(hash.substring(1));
    }
    // FECOA-1428/1430/1431: a scanned setup QR lands here with the Caregiver ID
    // (and, at creation time, the temporary PIN) in the query string. Pre-fill
    // both so the caregiver can sign in without manual entry.
    const params = new URLSearchParams(window.location.search);
    const cg = params.get('cg');
    const prefillPin = params.get('pin');
    if (cg) setCaregiverId(cg.toUpperCase());
    if (prefillPin) setPin(prefillPin);
    // FECOA-1514 (AC5): an idle logout redirects here with ?expired=1.
    if (params.get('expired')) setSessionExpired(true);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!caregiverId.trim() || !pin || !sponsorId.trim()) {
      setError('Caregiver ID, PIN, and sponsor are all required.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await caregiverService.login({
        caregiver_id: caregiverId.trim(),
        pin,
        sponsor_id: sponsorId.trim(),
      });
      if (result.requires_pin_change) {
        navigate('/caregiver/first-login');
      } else {
        navigate('/caregiver/dashboard');
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.error || err?.response?.data?.detail;
      if (status === 423) {
        setError(
          detail ||
            'Account locked. Please contact your study coordinator to reset your PIN.',
        );
      } else if (status === 401) {
        setError('Invalid Caregiver ID or PIN.');
      } else {
        setError(detail || 'Login failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full pl-10 pr-3 py-2.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-md text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500';

  // Suppress password-manager autofill/overlay icons. Caregiver ID + PIN are
  // not browser credentials, so LastPass/1Password/etc. wrongly inject the
  // saved email here. These per-manager opt-out attributes hide the icons.
  const noPwManager = {
    autoComplete: 'off',
    'data-lpignore': 'true',
    'data-1p-ignore': 'true',
    'data-bwignore': 'true',
    'data-form-type': 'other',
  };

  return (
    <div
      // pt-8 absorbs the LocalDevBanner; calc(...) keeps the page exactly the
      // available viewport height so vertical centering doesn't stack offsets.
      className="min-h-screen pt-8 bg-gray-50 dark:bg-slate-950 flex flex-col items-center justify-center px-4"
    >
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-6">
          <img src={flexcoaLogo} alt="flexCOA" className="h-10 w-auto mb-3" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Caregiver Sign In</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 text-center">
            Use the Caregiver ID and PIN your study coordinator gave you.
          </p>
        </div>

        {sessionExpired && (
          <div className="mb-4 text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30 rounded-md px-3 py-2.5">
            Your session expired due to inactivity. Please sign in again.
          </div>
        )}

        <form
          onSubmit={submit}
          className="bg-white dark:bg-slate-900/80 border border-gray-200 dark:border-slate-800 rounded-xl shadow-lg p-6 space-y-4"
        >
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
              Caregiver ID
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={caregiverId}
                onChange={(e) => setCaregiverId(e.target.value.toUpperCase())}
                placeholder="CG-00123"
                {...noPwManager}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
              PIN
            </label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type={showPin ? 'text' : 'password'}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                {...noPwManager}
                inputMode="numeric"
                className={inputCls.replace('pr-3', 'pr-10')}
              />
              <button
                type="button"
                onClick={() => setShowPin((s) => !s)}
                aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {!sponsorPrefilled && (
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                Sponsor
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  value={sponsorId}
                  onChange={(e) => setSponsorId(e.target.value)}
                  placeholder="Sponsor ID"
                  {...noPwManager}
                  className={inputCls}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Your study coordinator can confirm your sponsor ID.
              </p>
            </div>
          )}

          {error && (
            <p className="text-xs text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10 border border-rose-300 dark:border-rose-500/30 rounded-md px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full px-4 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium rounded-md text-sm flex items-center justify-center"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign In'}
          </button>
        </form>

        <p className="text-xs text-gray-500 text-center mt-4">
          Forgot your PIN?{' '}
          <span className="text-gray-700 dark:text-gray-300">Contact your study coordinator.</span>
        </p>
      </div>
    </div>
  );
};

export default CaregiverLogin;
